import React, { useEffect, useRef } from 'react';
import { useTrainingSocket } from '../../context/TrainingSocketContext';
import { socketService } from '../../services/trainingSocketService';
import ThreeNetworkRendererClass from '../ThreeNetworkRendererClass';
import { TrainingProgressData } from '../../services/trainingSocketService';
import './NetworkVisualizationComponent.css';

interface NetworkVisualizationComponentProps {
  modelStructure?: any;
  trainingProgressData?: TrainingProgressData[];
}

/**
 * NetworkVisualizationComponent
 *
 * Renders a 3D visualization of the neural network being trained.
 * Receives model structure and training data as props from TrainingProgressDisplay.
 */
const NetworkVisualizationComponent: React.FC<NetworkVisualizationComponentProps> = ({
  modelStructure,
  trainingProgressData = []
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<ThreeNetworkRendererClass | null>(null);
  const hasRequestedStructure = useRef(false);
  const { modelStructure: contextModelStructure } = useTrainingSocket();

  // Request model structure from backend when component mounts
  useEffect(() => {
    if (!hasRequestedStructure.current) {
      hasRequestedStructure.current = true;
      console.log('[NetworkViz] Component mounted, requesting model structure from backend');
      // Request the model structure from the backend
      socketService.emit('requestModelStructure');
    }
  }, []);

  // Initialize renderer with model structure (from context or props)
  useEffect(() => {
    const structure = contextModelStructure || modelStructure;

    if (containerRef.current && structure && !rendererRef.current) {
      console.log('[NetworkViz] Initializing renderer with model structure');
      // Initialize ThreeNetworkRenderer (no WebSocket needed - we get data via props)
      rendererRef.current = new ThreeNetworkRendererClass(
        containerRef.current
      );

      const renderer = rendererRef.current;

      // Set the model structure from context or props
      renderer.setNetworkStructure(structure);

      // Handle window resize
      const handleResize = () => {
        renderer.handleResize();
      };

      window.addEventListener('resize', handleResize);
      handleResize(); // Initial sizing

      return () => {
        window.removeEventListener('resize', handleResize);
        renderer.cleanup();
      };
    }
  }, [contextModelStructure, modelStructure]);

  // Update visualization when training progress data changes
  useEffect(() => {
    if (rendererRef.current && trainingProgressData && trainingProgressData.length > 0) {
      const latestData = trainingProgressData[trainingProgressData.length - 1];
      rendererRef.current.updateTrainingData(latestData);
    }
  }, [trainingProgressData]);

  return (
    <div
      ref={containerRef}
      className="network-visualization-component"
      style={{ width: '100%', height: '100%' }}
    />
  );
};

export default NetworkVisualizationComponent;
